import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';

export interface HeatmapCell {
  industry: string;
  threatType: string;
  count: number;
}

const NAICS_SECTOR_NAMES: Record<string, string> = {
    '11': 'Agriculture, Forestry, Fishing and Hunting',
    '21': 'Mining, Quarrying, and Oil and Gas Extraction',
    '22': 'Utilities',
    '23': 'Construction',
    '31': 'Manufacturing', '32': 'Manufacturing', '33': 'Manufacturing',
    '42': 'Wholesale Trade',
    '44': 'Retail Trade', '45': 'Retail Trade',
    '48': 'Transportation and Warehousing', '49': 'Transportation and Warehousing',
    '51': 'Information',
    '52': 'Finance and Insurance',
    '53': 'Real Estate and Rental and Leasing',
    '54': 'Professional, Scientific, and Technical Services',
    '55': 'Management of Companies and Enterprises',
    '56': 'Administrative and Support and Waste Management and Remediation Services',
    '61': 'Educational Services',
    '62': 'Health Care and Social Assistance',
    '71': 'Arts, Entertainment, and Recreation',
    '72': 'Accommodation and Food Services',
    '81': 'Other Services',
    '92': 'Public Administration'
};

@Component({
  selector: 'app-industry-threat-heatmap',
  templateUrl: './industry-threat-heatmap.component.html',
  styleUrls: ['./industry-threat-heatmap.component.css'],
  standalone: false
})
export class IndustryThreatHeatmapComponent implements OnInit, OnChanges {
  @Input() isDarkTheme = false;
  @Input() selectedIndustry?: string;
  @Input() selectedCountry?: string;
  @Input() data: any = [];
  @Input() totalThreats = 0;
  @Input() totalRecords = 0;

  // Data
  heatmapData: HeatmapCell[] = [];

  // Industries and Threat Types for headers
  industries: string[] = [];
  threatTypes: string[] = [
    'Attack',
    'Scan',
    'Malware',
    'Bot',
    'Spam',
    'C2',
    'Reflector',
    'Proxy',
    'Suspicious',
    'Unknown'
  ];

  // Industry row limit
  industryLimit = 5;
  industryLimitLabel = 'Top 5';

  // Minimize/expand state
  isMinimized = false;

  // Shared count scale used by both legend and cell colors
  // Buckets are % of selected cap: 5, 10, 20, 40, 60, 80
  private countBucketUpperBounds: number[] = [500, 1000, 2000, 4000, 6000, 8000];
  countLegendLabels: string[] = [];

  constructor() {}

  ngOnInit(): void {
    this.init_empty_naics();
    this.rebuildCountLegend();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.build_heatmap_from_data();
    } else if (changes['totalThreats'] || changes['totalRecords']) {
      this.updateCountScaleFromData();
    }
  }

  private build_heatmap_from_data(): void {
    if (!this.data || (Array.isArray(this.data) && !this.data.length)) {
      this.init_empty_naics();
      return;
    }

    const cell_map: Record<string, { count: number }> = {};

    if (Array.isArray(this.data)) {
      // Backward compatibility: res[4] = [{hour, naics_sector, threat_type, avg_value, count}]
      for (const entry of this.data) {
        const industry = this.resolveIndustry(entry.naics_sector);
        const threatType = this.resolveThreatType(entry.threat_type);
        this.addCellEntry(cell_map, industry, threatType, entry.count);
      }
    } else {
      // New direct matrix shape:
      // { "51": { total_count, total_avg_value, Attack: {count, avg_value}, ... }, ... }
      for (const [sector, sectorData] of Object.entries(this.data)) {
        const industry = this.resolveIndustry(sector);
        if (!sectorData || typeof sectorData !== 'object') {
          continue;
        }

        for (const [threatKey, threatMetrics] of Object.entries(sectorData as Record<string, any>)) {
          if (threatKey === 'total_count' || threatKey === 'total_avg_value') {
            continue;
          }
          if (!threatMetrics || typeof threatMetrics !== 'object') {
            continue;
          }

          const threatType = this.resolveThreatType(threatKey);
          const metrics = threatMetrics as Record<string, unknown>;
          this.addCellEntry(
            cell_map,
            industry,
            threatType,
            Number(metrics['count'] || 0)
          );
        }
      }
    }

    if (Object.keys(cell_map).length === 0) {
      this.init_empty_naics();
      return;
    }

    this.heatmapData = Object.keys(cell_map).map((key: string) => {
      const parts = key.split('|');
      const cell = cell_map[key];
      return {
        industry: parts[0],
        threatType: parts[1],
        count: cell.count
      };
    });

    this.industries = Array.from(new Set(this.heatmapData.map(d => d.industry))).sort();
    this.updateCountScaleFromData();
  }

  private init_empty_naics(): void {
    this.heatmapData = [];
    this.industries = [...Array.from(new Set(Object.values(NAICS_SECTOR_NAMES))).sort(), 'Unknown'];
    this.countBucketUpperBounds = [500, 1000, 2000, 4000, 6000, 8000];
    this.rebuildCountLegend();
  }

  private resolveIndustry(sector: string | undefined): string {
    const resolved = sector || 'Unknown';
    return resolved === 'Unknown' ? 'Unknown' : (NAICS_SECTOR_NAMES[resolved] || ('NAICS ' + resolved));
  }

  private resolveThreatType(threatType: string | undefined): string {
    return threatType && this.threatTypes.includes(threatType) ? threatType : 'Unknown';
  }

  private addCellEntry(
    cellMap: Record<string, { count: number }>,
    industry: string,
    threatType: string,
    rawCount: number
  ): void {
    const key = industry + '|' + threatType;
    if (!cellMap[key]) {
      cellMap[key] = { count: 0 };
    }
    cellMap[key].count += Number(rawCount || 0);
  }

  onDisplayModeChange(_mode: string): void { /* no-op: only count mode remains */ }

  /**
   * Get a cell from the heatmap by industry and threat type
   */
  getCell(industry: string, threatType: string): HeatmapCell | undefined {
    return this.heatmapData.find(
      d => d.industry === industry && d.threatType === threatType
    );
  }

  /**
   * Get the display value for a cell
   */
  getCellValue(cell: HeatmapCell | undefined): string | number {
    if (!cell) return '-';
    return cell.count;
  }

  /**
   * Get the color for a cell
   */
  getCellColor(cell: HeatmapCell | undefined): string {
    if (!cell) return this.isDarkTheme ? '#2a2a2a' : '#f0f0f0';

    return this.getCountColor(cell.count);
  }

  getCountLegendColor(index: number): string {
    const darkColors = ['#0d2b3e', '#0e4d78', '#1565c0', '#9a6600', '#cc5500', '#b23000', '#8b1a1a'];
    const lightColors = ['#E8F4F8', '#B8E0FF', '#5BA3FF', '#FFD580', '#FFB347', '#FF8C3D', '#D32F2F'];
    const palette = this.isDarkTheme ? darkColors : lightColors;
    return palette[Math.max(0, Math.min(index, palette.length - 1))];
  }

  private getCountColor(count: number): string {
    const bucketIdx = this.getCountBucketIndex(count);
    return this.getCountLegendColor(bucketIdx);
  }

  private getCountBucketIndex(count: number): number {
    for (let i = 0; i < this.countBucketUpperBounds.length; i++) {
      if (count <= this.countBucketUpperBounds[i]) {
        return i;
      }
    }
    return this.countBucketUpperBounds.length;
  }

  private updateCountScaleFromData(): void {
    const nonZeroCounts = this.heatmapData
      .map(c => Number(c.count || 0))
      .filter(v => v > 0);

    const dataMaxCount = nonZeroCounts.length ? Math.max(...nonZeroCounts) : 0;
    const threatBaseline = Number(this.totalThreats || 0);
    const recordBaseline = Number(this.totalRecords || 0);
    const roundedThreatCap = this.roundToCleanMod10(threatBaseline);
    const roundedRecordCap = this.roundToCleanMod10(recordBaseline);
    const cap = Math.max(1, roundedThreatCap || roundedRecordCap || dataMaxCount || 10000);

    if (!nonZeroCounts.length && !this.totalThreats) {
      this.countBucketUpperBounds = [500, 1000, 2000, 4000, 6000, 8000];
      this.rebuildCountLegend();
      return;
    }

    // Use fixed percentage anchors of cap: 5, 10, 20, 40, 60, 80%
    const percentages = [0.05, 0.10, 0.20, 0.40, 0.60, 0.80];
    this.countBucketUpperBounds = percentages.map(p => {
      const raw = cap * p;
      return Math.max(1, this.roundThreshold(raw));
    });

    // Enforce strict increase
    for (let i = 1; i < this.countBucketUpperBounds.length; i++) {
      if (this.countBucketUpperBounds[i] <= this.countBucketUpperBounds[i - 1]) {
        this.countBucketUpperBounds[i] = this.countBucketUpperBounds[i - 1] + 1;
      }
    }

    this.rebuildCountLegend();
  }

  private roundToCleanMod10(value: number): number {
    const n = Math.max(0, Math.round(value || 0));
    if (n <= 10) {
      return 10;
    }
    const magnitude = Math.pow(10, Math.floor(Math.log10(n)));
    const step = Math.max(10, magnitude / 10);
    return Math.round(n / step) * step;
  }

  private rebuildCountLegend(): void {
    const b = this.countBucketUpperBounds;
    if (!b.length) {
      this.countLegendLabels = [];
      return;
    }

    const labels: string[] = [`<= ${this.formatCount(b[0])}`];
    for (let i = 1; i < b.length; i++) {
      labels.push(`${this.formatCount(b[i - 1] + 1)}-${this.formatCount(b[i])}`);
    }
    labels.push(`> ${this.formatCount(b[b.length - 1])}`);
    this.countLegendLabels = labels;
  }

  private formatCount(value: number): string {
    return Math.max(0, Math.round(value)).toLocaleString('en-US');
  }

  private roundThreshold(value: number): number {
    if (value < 1000) {
      return Math.round(value / 10) * 10;
    }
    return Math.round(value / 500) * 500;
  }

  changeIndustryLimit(limit: number, label: string): void {
    this.industryLimit = limit;
    this.industryLimitLabel = label;
  }

  /**
   * Get unique industries from sorted data for row headers
   */
  getSortedIndustries(): string[] {
    const industry_totals: Record<string, number> = {};

    const all_naics_sectors = Array.from(new Set(Object.values(NAICS_SECTOR_NAMES)));
    for (let i = 0; i < all_naics_sectors.length; i++) {
      industry_totals[all_naics_sectors[i]] = 0;
    }
    industry_totals['Unknown'] = 0;

    for (let i = 0; i < this.heatmapData.length; i++) {
      const cell = this.heatmapData[i];
      if (!industry_totals[cell.industry]) {
        industry_totals[cell.industry] = 0;
      }
      industry_totals[cell.industry] += cell.count;
    }

    const all_industries = Object.keys(industry_totals);

    // Sort all industries by count descending, including Unknown
    const sorted_industries = all_industries.sort((a, b) => industry_totals[b] - industry_totals[a]);

    if (this.industryLimit > 0) {
      // For Top 5/10: only return industries that have data
      const with_data = sorted_industries.filter(ind => industry_totals[ind] > 0);
      return with_data.slice(0, this.industryLimit);
    }
    return sorted_industries;
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
  }
}
